import React, { useState } from "react";
import moment from "moment";
import { HiOutlineTrash, HiOutlineInboxStack } from "react-icons/hi2";
import { Modal } from "antd";

import { developerApi } from "../../../api/developer-tools";
import { notify } from "../../../util";

import "./keys-table.scss";

interface IKeysTableProps {
  keys: any[];
  isLoading: boolean;
  onDelete?: () => void;
}

export const KeysTable = ({
  isLoading,
  keys,
  onDelete,
}: IKeysTableProps): JSX.Element => {
  const { confirm: confirmAction } = Modal;
  const [isProcessingRequest, setIsProcessingRequest] =
    useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set([0]));

  const toggleRow = (index: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleDeleteKey = (id: string) => {
    setIsProcessingRequest(true);
    developerApi
      .deleteKey(id)
      .then(() => {
        notify("success", "API key have been deleted!");
        onDelete && onDelete();
      })
      .finally(() => setIsProcessingRequest(false));
  };

  const showDeleteConfirmation = (id: string): void => {
    confirmAction({
      title: "Are you sure you want to delete key?",
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { className: "cancel-button" },
      onOk() {
        handleDeleteKey(id);
      },
      onCancel() {},
    });
  };

  const loading = isLoading || isProcessingRequest;

  if (loading) {
    return (
      <div className="keys-table simple-table">
        <div className="keys-table__header simple-table__header">
          <div className="simple-table__col">Key</div>
          <div className="simple-table__col">Creator</div>
          <div className="simple-table__col">Date</div>
          <div className="simple-table__col simple-table__col--right">Action</div>
        </div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="keys-table__row simple-table__row simple-table__row--expanded">
            <div className="simple-table__col" data-label="Key">
              <span className="keys-table__shimmer" style={{ width: 200, height: 14 }} />
            </div>
            <div className="simple-table__col" data-label="Creator">
              <span className="keys-table__shimmer" style={{ width: 140, height: 14 }} />
            </div>
            <div className="simple-table__col" data-label="Date">
              <span className="keys-table__shimmer" style={{ width: 100, height: 14 }} />
            </div>
            <div className="simple-table__col simple-table__col--right" data-label="Actions">
              <span className="keys-table__shimmer" style={{ width: 32, height: 32, borderRadius: 8 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!keys?.length) {
    return (
      <div className="keys-table simple-table">
        <div className="keys-table__empty">
          <HiOutlineInboxStack size={32} />
          <span>No API keys yet</span>
        </div>
      </div>
    );
  }

  return (
    <div className="keys-table simple-table">
      <div className="keys-table__header simple-table__header">
        <div className="simple-table__col">Key</div>
        <div className="simple-table__col">Creator</div>
        <div className="simple-table__col">Date</div>
        <div className="simple-table__col simple-table__col--right">Action</div>
      </div>
      {keys.map((key: any, index: number) => (
        <div
          key={key.id}
          className={`keys-table__row simple-table__row${expandedRows.has(index) ? " simple-table__row--expanded" : ""}`}
          onClick={(e) => {
            const header = e.currentTarget.firstElementChild;
            if (header && header.contains(e.target as Node)) toggleRow(index);
          }}
        >
          <div className="simple-table__col simple-table__col--primary keys-table__key" data-label="Key">
            {key.id}...
          </div>
          <div className="simple-table__col" data-label="Creator">{key.created_by}</div>
          <div className="simple-table__col" data-label="Date">
            {moment(key.created_at).format("MMM Do, HH:mm")}
          </div>
          <div className="simple-table__col simple-table__col--right" data-label="Actions">
            <button
              className="keys-table__delete-btn"
              onClick={() => showDeleteConfirmation(key.id)}
            >
              <HiOutlineTrash size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
